import type { ReactNode } from "react";

import { cn } from "../lib/cn";

export interface StatTileProps {
  /** The figure itself. A string, so a rate can render as "92%" and a gap as "—". */
  value: string;
  /** The uppercase caption beneath it — "Birds", "Pairs", "Eggs", "Ringed". */
  label: string;
  icon?: ReactNode;
  /**
   * What the figure is made of — "33 cocks · 37 hens" under a total of 70.
   *
   * Drawn from `lg` only. It is the line the phone tile has no room for, and the line
   * the desktop deck looks empty without: a 336px column holding four bare numbers is a
   * lot of card around very little fact.
   */
  hint?: string | undefined;
  className?: string;
}

/**
 * One figure in the totals strip: icon, figure, caption, in that order at every width.
 *
 * Four of these share a row on a 390px phone, so the tile has to survive at about 80px
 * wide: stacked and centred, the caption at 10px, no qualifying line. In the desktop deck
 * two share a 360px column, so the stack aligns left, the figure grows, and the hint gets
 * its line.
 */
export function StatTile({ value, label, icon, hint, className }: StatTileProps) {
  return (
    <div
      className={cn(
        "flex flex-col items-center gap-hair rounded-inset bg-surface-container-lowest",
        "px-1 py-md shadow-card",
        "lg:items-start lg:gap-1 lg:rounded-card lg:p-4",
        className,
      )}
    >
      {icon ? (
        <span aria-hidden className="text-primary">
          {icon}
        </span>
      ) : null}

      <span className="text-tile text-on-surface lg:text-tile-wide" data-numeric>
        {value}
      </span>

      <span className="text-center text-tile-label uppercase text-on-surface-variant lg:text-left lg:text-tile-label-wide">
        {label}
      </span>

      {hint ? (
        <span className="hidden text-caption text-on-surface-body lg:block" data-numeric>
          {hint}
        </span>
      ) : null}
    </div>
  );
}
